type Diagnostic = {
  at: string;
  event: string;
  durationMs?: number;
  status?: string;
  provider?: string;
  queueMs?: number;
  processingMs?: number;
};
const key = 'enactry.diagnostics.v1';
const events = [
  'stt',
  'tutor',
  'tts',
  'speech-start',
  'play',
  'replay',
  'pause',
  'note-open',
  'note-delete',
  'voice-qualification',
  'voice-endpoint',
  'voice-candidate-rejected',
  'voice-utterance-rejected',
  'audio-start',
] as const;
type DiagnosticEvent = (typeof events)[number];
/** Bounded device-local technical metadata. Never accept text, audio or equations. */
export function diagnostic(
  event: DiagnosticEvent,
  durationMs?: number,
  status?: 'ok' | 'aborted' | 'error' | 'empty' | 'cached',
  metadata?: {
    provider?: string;
    timingMs?: { queue: number; processing: number };
  },
) {
  try {
    const entries = readDiagnostics();
    entries.push({
      at: new Date().toISOString(),
      event,
      ...(Number.isFinite(durationMs)
        ? { durationMs: Math.max(0, Math.round(durationMs!)) }
        : {}),
      ...(status ? { status } : {}),
      ...(metadata?.provider && /^[a-z0-9_.\/-]{1,80}$/i.test(metadata.provider)
        ? { provider: metadata.provider }
        : {}),
      ...(Number.isFinite(metadata?.timingMs?.queue)
        ? { queueMs: Math.max(0, Math.round(metadata!.timingMs!.queue)) }
        : {}),
      ...(Number.isFinite(metadata?.timingMs?.processing)
        ? {
            processingMs: Math.max(
              0,
              Math.round(metadata!.timingMs!.processing),
            ),
          }
        : {}),
    });
    localStorage.setItem(key, JSON.stringify(entries.slice(-200)));
  } catch {}
}
export function readDiagnostics(): Diagnostic[] {
  try {
    const rows = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(rows)
      ? rows
          .filter(
            (r) => r && typeof r.at === 'string' && typeof r.event === 'string',
          )
          .slice(-200)
          .map((r) => ({
            at: r.at.slice(0, 40),
            event: events.includes(r.event) ? r.event : 'unknown',
            ...(Number.isFinite(r.durationMs)
              ? { durationMs: Math.max(0, r.durationMs) }
              : {}),
            ...(['ok', 'aborted', 'error', 'empty', 'cached'].includes(r.status)
              ? { status: r.status }
              : {}),
            ...(typeof r.provider === 'string' &&
            /^[a-z0-9_.\/-]{1,80}$/i.test(r.provider)
              ? { provider: r.provider }
              : {}),
            ...(Number.isFinite(r.queueMs)
              ? { queueMs: Math.max(0, r.queueMs) }
              : {}),
            ...(Number.isFinite(r.processingMs)
              ? { processingMs: Math.max(0, r.processingMs) }
              : {}),
          }))
      : [];
  } catch {
    return [];
  }
}
/** Separate stages rather than adding unrelated requests into a false total. */
export function summarizeDiagnostics(rows = readDiagnostics()) {
  const stages = [
    'voice-qualification',
    'voice-endpoint',
    'stt',
    'tutor',
    'tts',
    'audio-start',
  ] as const;
  return Object.fromEntries(
    stages.map((event) => {
      const matching = rows.filter((row) => row.event === event);
      const durations = matching
        .filter((row) => row.status === 'ok' && Number.isFinite(row.durationMs))
        .map((row) => row.durationMs!)
        .sort((a, b) => a - b);
      return [
        event,
        {
          completed: durations.length,
          cached: matching.filter((row) => row.status === 'cached').length,
          aborted: matching.filter((row) => row.status === 'aborted').length,
          errors: matching.filter((row) => row.status === 'error').length,
          empty: matching.filter((row) => row.status === 'empty').length,
          ...(durations.length
            ? {
                medianMs:
                  durations.length % 2
                    ? durations[Math.floor(durations.length / 2)]
                    : (durations[durations.length / 2 - 1]! +
                        durations[durations.length / 2]!) /
                      2,
                p95Ms: durations[Math.ceil(durations.length * 0.95) - 1],
              }
            : {}),
        },
      ];
    }),
  );
}
/** One playback request, ending at actual media playing, rejection or cancellation. */
export function createAudioStartDiagnostic(now = () => performance.now()) {
  let pending: { id: number; at: number } | undefined;
  function finish(status: 'ok' | 'aborted' | 'error', id?: number) {
    if (!pending || (id !== undefined && pending.id !== id)) return;
    diagnostic('audio-start', now() - pending.at, status);
    pending = undefined;
  }
  return {
    begin(id: number) {
      finish('aborted');
      pending = { id, at: now() };
    },
    finish,
  };
}
export function downloadDiagnostics() {
  const url = URL.createObjectURL(
    new Blob(
      [
        JSON.stringify(
          {
            version: 1,
            scope:
              'Device-local technical timings; no microphone audio or conversation text',
            timingNote:
              'Recent bounded events, not a latency benchmark. Voice qualification and endpoint use captured sample duration. Other stages use elapsed time. Stages are not correlated into a turn total.',
            summary: summarizeDiagnostics(),
            events: readDiagnostics(),
          },
          null,
          2,
        ),
      ],
      { type: 'application/json' },
    ),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = 'enactry-troubleshooting.json';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
